import { Node } from 'reactflow';
import {
  SubLayersWithoutValidStatus,
  SubLayersWithScopeFilterOnly,
  SubLayerTypes,
} from '@constants/canvas/layers';
import {
  CiaFilters,
  ProjectFiltersContextProps,
} from '@context/ProjectFilters/types';
import { isCriteriaFieldsMatch } from '@utils/canvasHelpers';

export type ModelFilters = Pick<
  ProjectFiltersContextProps,
  | 'validationStatus'
  | 'reviewStatus'
  | 'operationalStatuses'
  | 'operatingModelStatuses'
  | 'partners'
  | 'ciaFilters'
>;

const hasCiaSelection = (ciaFilters: CiaFilters) =>
  Object.values(ciaFilters).some((fields) =>
    Object.values(fields).some((answers) => answers?.length),
  );

const hasActiveModelFilter = ({
  validationStatus,
  reviewStatus,
  operationalStatuses,
  operatingModelStatuses,
  partners,
  ciaFilters,
}: ModelFilters) =>
  Boolean(
    validationStatus.length ||
      reviewStatus.length ||
      operationalStatuses.length ||
      operatingModelStatuses.length ||
      partners.length ||
      hasCiaSelection(ciaFilters),
  );

export const matchesModelFilters = (node: Node, filters: ModelFilters) => {
  const {
    validationStatus,
    reviewStatus,
    operationalStatuses,
    operatingModelStatuses,
    partners,
    ciaFilters,
  } = filters;

  const dto = node.data?.dto;

  if (SubLayersWithScopeFilterOnly.includes(node.parentNode as SubLayerTypes)) {
    return !hasActiveModelFilter(filters);
  }

  if (reviewStatus.length && !dto?.review_required) return false;

  if (validationStatus.length) {
    const hasNoValidStatus = SubLayersWithoutValidStatus.includes(
      node.parentNode as SubLayerTypes,
    );

    if (hasNoValidStatus || !validationStatus.includes(dto?.valid_status)) {
      return false;
    }
  }

  if (
    operationalStatuses.length &&
    !operationalStatuses.includes(dto?.meta_data?.operational_status)
  ) {
    return false;
  }

  if (
    operatingModelStatuses.length &&
    !operatingModelStatuses.includes(dto?.meta_data?.operational_model)
  ) {
    return false;
  }

  if (
    partners.length &&
    !partners.some((partner) => dto?.partners?.includes(partner))
  ) {
    return false;
  }

  if (hasCiaSelection(ciaFilters)) {
    const isAllMatch =
      isCriteriaFieldsMatch(
        ciaFilters.assurance_level,
        dto?.cia?.assurance_level?.criterias,
      ) &&
      isCriteriaFieldsMatch(
        ciaFilters.coverage,
        dto?.cia?.coverage?.criterias,
      ) &&
      isCriteriaFieldsMatch(
        ciaFilters.applicability,
        dto?.cia?.applicability?.criterias,
      ) &&
      dto?.is_reported !== false;

    if (!isAllMatch) return false;
  }

  return true;
};
